import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ListCard from '../components/lists/ListCard';
import { LIST_CATEGORIES, categoryLabel } from '../constants/listCategories';
import { useWorkspace } from '../context/WorkspaceContext';
import {
  addListItem,
  createList,
  deleteList,
  deleteListItem,
  getLists,
  renameList,
  updateList,
  updateListItem,
} from '../utils/listsApi';

/**
 * The Scratchpad: a person's private space for rough thoughts, bugs, and ideas,
 * kept as multiple named lists and scoped to whichever organization they are in.
 * Only the signed-in user ever sees their own lists. Mirrors the Shape Up page's
 * shape — org selection, optimistic handlers that revert on error, a loading
 * skeleton, an empty state, and a bottom snackbar for failures.
 */
const ScratchpadPage = () => {
  const { organizations, currentOrganization } = useWorkspace();

  const [organizationId, setOrganizationId] = useState('');
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [newListOpen, setNewListOpen] = useState(false);
  const [newListTitle, setNewListTitle] = useState('');
  const [newListCategory, setNewListCategory] = useState('');
  // The active tab is a string value — 'all' or a category key — so it stays
  // stable when the list order changes, unlike a numeric tab index.
  const [activeTab, setActiveTab] = useState('all');

  // Follow the workspace's current org, falling back to the first one, so the
  // lists shown always belong to the org the person is looking at.
  useEffect(() => {
    if (currentOrganization?.id) {
      setOrganizationId(currentOrganization.id);
    } else if (organizations.length > 0) {
      setOrganizationId(organizations[0].id);
    } else {
      setLoading(false);
    }
  }, [currentOrganization, organizations]);

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getLists({ organizationId })
      .then((data) => {
        if (!cancelled) setLists(data.lists || []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Could not load your lists.');
          setLists([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [organizationId]);

  // Replace one list in place, so a handler can patch just the list it touched
  // without rebuilding the whole set.
  const patchList = useCallback((listId, updater) => {
    setLists(prev => prev.map(l => (l.id === listId ? updater(l) : l)));
  }, []);

  const handleCreateList = useCallback(async () => {
    const title = newListTitle.trim();
    if (!title) return;
    const category = newListCategory || null;
    setNewListOpen(false);
    setNewListTitle('');
    try {
      const created = await createList({ organizationId, title, category });
      setLists(prev => [...prev, { ...created, items: created.items || [] }]);
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not create the list.' });
    }
  }, [newListTitle, newListCategory, organizationId]);

  const handleRenameList = useCallback(async (list, title) => {
    const previous = list.title;
    patchList(list.id, l => ({ ...l, title }));
    try {
      await renameList(list.id, title);
    } catch (err) {
      patchList(list.id, l => ({ ...l, title: previous }));
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not rename the list.' });
    }
  }, [patchList]);

  const handleDeleteList = useCallback(async (list) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the list "${list.title}" and its items?`)) return;
    const previous = lists;
    setLists(prev => prev.filter(l => l.id !== list.id));
    try {
      await deleteList(list.id);
    } catch (err) {
      setLists(previous);
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not delete the list.' });
    }
  }, [lists]);

  const handleChangeCategory = useCallback(async (list, category) => {
    const next = category || null;
    if (next === (list.category ?? null)) return;
    const previous = list.category ?? null;
    patchList(list.id, l => ({ ...l, category: next }));
    try {
      await updateList(list.id, { category: next });
    } catch (err) {
      patchList(list.id, l => ({ ...l, category: previous }));
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not move the list.' });
    }
  }, [patchList]);

  const handleAddItem = useCallback(async (list, content) => {
    try {
      const item = await addListItem(list.id, content);
      patchList(list.id, l => ({ ...l, items: [...(l.items || []), item] }));
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not add the item.' });
    }
  }, [patchList]);

  const handleToggleItemDone = useCallback(async (list, item, done) => {
    patchList(list.id, l => ({
      ...l,
      items: l.items.map(i => (i.id === item.id ? { ...i, done } : i)),
    }));
    try {
      await updateListItem(item.id, { done });
    } catch (err) {
      patchList(list.id, l => ({
        ...l,
        items: l.items.map(i => (i.id === item.id ? { ...i, done: !done } : i)),
      }));
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not update the item.' });
    }
  }, [patchList]);

  const handleEditItemContent = useCallback(async (list, item, content) => {
    const previous = item.content;
    patchList(list.id, l => ({
      ...l,
      items: l.items.map(i => (i.id === item.id ? { ...i, content } : i)),
    }));
    try {
      await updateListItem(item.id, { content });
    } catch (err) {
      patchList(list.id, l => ({
        ...l,
        items: l.items.map(i => (i.id === item.id ? { ...i, content: previous } : i)),
      }));
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not edit the item.' });
    }
  }, [patchList]);

  const handleDeleteItem = useCallback(async (list, item) => {
    const previousItems = list.items;
    patchList(list.id, l => ({ ...l, items: l.items.filter(i => i.id !== item.id) }));
    try {
      await deleteListItem(item.id);
    } catch (err) {
      patchList(list.id, l => ({ ...l, items: previousItems }));
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not delete the item.' });
    }
  }, [patchList]);

  const countFor = (key) => lists.filter(l => l.category === key).length;
  const visibleLists = activeTab === 'all'
    ? lists
    : lists.filter(l => l.category === activeTab);
  const activeLabel = categoryLabel(activeTab);

  if (!loading && organizations.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <FormatListBulletedIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h5" gutterBottom>Scratchpad needs an organization</Typography>
          <Typography variant="body1" color="text.secondary">
            Your private lists belong to an organization, so they follow the workspace you are in.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormatListBulletedIcon />
              My Ideas
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your private space to capture rough thoughts before they're ready to share —
              bugs, feature ideas, pitches, and tech debt you want to remember. Organize
              notes into lists, file them under categories like Pitches, Cooldown, Tech
              Debt, and Ideas, and check items off as you work through them. Everything
              here is scoped to the selected organization and visible only to you.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {organizations.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={organizationId}
                  label="Organization"
                  onChange={(e) => setOrganizationId(e.target.value)}
                >
                  {organizations.map(org => (
                    <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setNewListTitle('');
                setNewListCategory(activeTab === 'all' ? '' : activeTab);
                setNewListOpen(true);
              }}
              disabled={!organizationId}
            >
              New list
            </Button>
          </Box>
        </Box>
      </Paper>

      {!loading && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Scratchpad categories"
          >
            <Tab value="all" label={`All (${lists.length})`} />
            {LIST_CATEGORIES.map(c => (
              <Tab key={c.key} value={c.key} label={`${c.label} (${countFor(c.key)})`} />
            ))}
          </Tabs>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {[0, 1, 2].map(i => (
            <Skeleton key={i} variant="rounded" width={300} height={220} />
          ))}
        </Box>
      ) : visibleLists.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <FormatListBulletedIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          {activeLabel ? (
            <>
              <Typography variant="h6" gutterBottom>{`No lists in ${activeLabel} yet`}</Typography>
              <Typography variant="body2" color="text.secondary">
                {`Create one to start filing ${activeLabel} notes.`}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>No lists yet</Typography>
              <Typography variant="body2" color="text.secondary">
                Make a list — "Bugs", "Ideas", "Someday" — and start jotting.
              </Typography>
            </>
          )}
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {visibleLists.map(list => (
            <ListCard
              key={list.id}
              list={list}
              onRename={handleRenameList}
              onDelete={handleDeleteList}
              onChangeCategory={handleChangeCategory}
              onAddItem={handleAddItem}
              onToggleItemDone={handleToggleItemDone}
              onEditItemContent={handleEditItemContent}
              onDeleteItem={handleDeleteItem}
            />
          ))}
        </Box>
      )}

      <Dialog open={newListOpen} onClose={() => setNewListOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New list</DialogTitle>
        <DialogContent>
          <TextField
            value={newListTitle}
            onChange={(e) => setNewListTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateList(); } }}
            placeholder="e.g. Bugs, Ideas, Someday"
            label="List name"
            autoFocus
            fullWidth
            margin="dense"
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="new-list-category-label">Category</InputLabel>
            <Select
              labelId="new-list-category-label"
              value={newListCategory}
              label="Category"
              onChange={(e) => setNewListCategory(e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {LIST_CATEGORIES.map(c => (
                <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewListOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateList} disabled={!newListTitle.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Container>
  );
};

export default ScratchpadPage;
